import {
  notificationTypeMap,
  headerNotificationTabs as tabs,
} from "../../helpers/constants";
import { fetchAllNotifications } from "../../helpers/querycalls";
import { useSearchToggle } from "../../contexts/SearchContext";
import { useSidebar } from "../../contexts/SidebarContext";
import React, { useEffect, useRef, useState } from "react";
import { NotificationItem } from "./NotificationItem";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";

const Header = () => {
  const navigate = useNavigate();

  const location = useLocation();
  // =======================
  // Custom hooks
  // =======================
  const toggleSearchModal = useSearchToggle();
  const { toggleSidebar } = useSidebar();

  // =======================
  // Redux State Management
  // =======================
  // const { notifications, allCount, replyCount, mentionCount, reactionCount } = useSelector((state) => state.notifications);

  // =======================
  // useRefs
  // =======================
  const dropdownSettingsRef = useRef(null);
  const dropdownNotificationRef = useRef(null);

  // =======================
  // Admin Authentication
  // =======================
  const userObj = localStorage.getItem("salla_discourse_user");
  const user = JSON.parse(userObj);
  let userName = "";
  let avatarTemplate = "";
  let isAdmin = false;
  let adminLink = "";
  if (user) {
    userName = user.username;
    avatarTemplate = user.avatar_template.replace("{size}", "100");
    if (user.admin) {
      isAdmin = true;
      adminLink = process.env.REACT_APP_API_URL + "/admin";
    }
  }

  // =======================
  // useStates
  // =======================
  const [isSettingDropdownOpen, setIsSettingDropdownOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] =
    useState(false);
  const [selectedNotificationTab, setSelectedNotificationTab] =
    useState("الكل");

  // =======================
  // Framer Motion
  // =======================
  const popupAntimation = {
    initial: { y: 30, opacity: 0 },
    animate: {
      y: 5,
      opacity: 1,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    exit: { y: 30, opacity: 0, transition: { duration: 0.2, ease: "easeIn" } },
  };
  const [isMobile, setIsMobile] = useState(window.innerWidth < 767);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // =======================
  // useEffects
  // =======================
  // Close settings dropdown when clicking outside

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownSettingsRef.current &&
        !dropdownSettingsRef.current.contains(event.target) &&
        !event.target.closest(".setting-icon")
      ) {
        setIsSettingDropdownOpen(false);
        document.body.style.overflow = "unset";
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsSettingDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownNotificationRef.current &&
        !dropdownNotificationRef.current.contains(event.target) &&
        !event.target.closest(".bell-icon")
      ) {
        setIsNotificationDropdownOpen(false);
        document.body.style.overflow = "unset";
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
  }, [setIsNotificationDropdownOpen]);

  useEffect(() => {
    return () => {
      // Cleanup overflow when the component unmounts
      document.body.style.overflow = "unset";
    };
  }, []);

  // =======================
  // handlers
  // =======================
  async function logout() {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/session/${user.username}`,
        {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          params: {
            return_url: process.env.REACT_APP_API_URL.includes("stg")
              ? "https://stage-community.salla.com"
              : "https://community.salla.com",
          },
        }
      );

      localStorage.removeItem("salla_discourse_user");
      localStorage.removeItem("salla_discourse_token");
      navigate("/");
    } catch (error) {
      console.log(error);
    }
  }

  function notificationRedirect() {
    setIsNotificationDropdownOpen(false);
    navigate("/notifications");
  }

  function focusSearchBar() {
    const searchBar = document.getElementById("search-bar");
    window.scrollTo({ top: 0, behavior: "smooth" });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            searchBar.focus(); // Focus the element when it's fully in view
            observer.disconnect(); // Stop observing after focusing
          }
        });
      },
      { threshold: 1.0 } // Trigger only when 100% of the element is visible
    );

    observer.observe(searchBar);
  }

  const { data: allNotificationsData, refetch } = useQuery({
    queryKey: ["allNotifications"],
    queryFn: fetchAllNotifications,
    enabled: false, // Prevent auto-fetching
    staleTime: 30 * 1000, //15 seconds
  });
  const report = allNotificationsData?.reports;
  function handleClickNotificationBell() {
    setIsNotificationDropdownOpen((prev) => {
      const isOpening = !prev;
      if (isOpening) {
        setSelectedNotificationTab("الكل");
        refetch(); // Fetch notifications only when opening the dropdown
      }
      return isOpening;
    });

    document.body.style.overflow = !isNotificationDropdownOpen
      ? "hidden"
      : "unset";
  }

  async function handleTabChange(tabName) {
    setSelectedNotificationTab(tabName);
  }
  // const notifications = data?.notifications || [];
  const counts = allNotificationsData?.counts || {
    allCount: 0,
    replyCount: 0,
    mentionCount: 0,
    reactionCount: 0,
  };

  const filteredNotifications =
    selectedNotificationTab === "الكل"
      ? allNotificationsData?.notifications
      : allNotificationsData?.notifications?.filter((notification) => {
          if (selectedNotificationTab === "ردود")
            return notification.notification_type === 2;
          if (selectedNotificationTab === "إعجابات وتفاعل")
            return notification.notification_type === 25;
          if (selectedNotificationTab === "منشن")
            return notification.notification_type === 1;
          return false;
        });
  const unreadCount = allNotificationsData?.notifications.reduce(
    (acc, notification) => {
      acc += !notification.read;
      return acc;
    },
    0
  );
  return (
    <header className="sticky top-0 z-50 flex h-[89px] items-center justify-between sm:px-6 p-4  bg-[#f8f8f8] border-b">
      {/* Hamburger Icon for Mobile View */}
      {user ? (
        <div className="flex sm:gap-5 gap-3">
          <div
            className="cursor-pointer py-2 setting-icon flex items-center space-x-3 relative rounded-lg"
            onClick={() => setIsSettingDropdownOpen((prev) => !prev)}
          >
            <img
              src={avatarTemplate}
              alt="Profile"
              className="md:w-11 md:h-11 w-8 h-8 rounded-full border-2 sm:block cursor-pointer"
              onError={(e) => {
                e.target.src =
                  `${process.env.REACT_APP_API_URL}` + avatarTemplate;
              }}
            />
            <span className="text-base hidden sm:block font-medium text-[#444]">
              {user.name.length === 0 ? user.username : user.name}
            </span>
            <div className="relative hidden sm:block">
              <img
                src={`/images/header/arrow-down.svg`}
                alt=""
                className={`cursor-pointer block ${
                  isSettingDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </div>
            <AnimatePresence>
              {isSettingDropdownOpen && (
                <div
                  className={`${
                    setIsSettingDropdownOpen
                      ? "!ml-0 fixed inset-0 bg-[#004D5A] bg-opacity-50 sm:bg-transparent z-40"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsSettingDropdownOpen(false);
                  }}
                >
                  <motion.div
                    initial={isMobile ? { y: "100vh" } : { y: 30, opacity: 0 }}
                    animate={{
                      y: 0,
                      opacity: 1,
                      transition: { duration: 0.3 },
                    }}
                    exit={
                      isMobile
                        ? { y: "100vh", transition: { duration: 0.3 } }
                        : { y: 30, opacity: 0, transition: { duration: 0.3 } }
                    }
                    ref={dropdownSettingsRef}
                    className="fixed bottom-0 sm:bottom-auto sm:absolute sm:top-[52px] left-0 w-full right-0 sm:left-[26px] mt-[26px] sm:w-[352px] !ml-0 sm:ml-4 bg-white rounded-md border-[1px] border-[#F1F1F1] shadow-lg z-50"
                  >
                    {/* Triangle Arrow */}
                    <div className="hidden sm:block absolute top-[-9px] left-[14px] w-4 h-4 bg-white border-l border-t border-[#F1F1F1] transform rotate-45"></div>

                    {/* Dropdown Content */}
                    <ul className="pt-2 sm:w-[352px] w-full">
                      <li
                        className="flex justify-between px-4 py-3 text-sm text-gray-700 cursor-pointer border-b-[1px] border-[#F1F1F1] hover:bg-gray-100 rounded-md"
                        onClick={() => {
                          navigate("/profile", {
                            state: {
                              id: user.id,
                              username: userName,
                            },
                          });
                        }}
                      >
                        {/* <div className="w-full flex justify-start items-center pl-2">
                        <img
                          src="/images/header/logout.svg"
                          alt="logout"
                          className="hover:bg-gray-200 p-2 rounded-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            logout();
                          }}
                        />
                        <img
                          src="/images/header/pencil-edit.svg"
                          alt="edit"
                          className="hover:bg-gray-200 p-2 rounded-md"
                          onClick={(e) => {
                            setIsSettingDropdownOpen(false);
                            navigate("/settings");
                            e.stopPropagation();
                          }}
                        />
                      </div> */}
                        <div className="w-full flex justify-end items-center gap-3">
                          <p className="text-[15px] text-[#666666]">
                            الملف الشخصي
                          </p>
                          <img
                            src={avatarTemplate}
                            className="rounded-full w-[24px] h-[24px]"
                            onError={(e) => {
                              e.target.src =
                                `${process.env.REACT_APP_API_URL}` +
                                avatarTemplate;
                            }}
                            alt="avatar"
                          />
                        </div>
                      </li>
                      {/* <li
                      className="w-full flex justify-end items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer border-b-[1px] border-[#F1F1F1]"
                      onClick={() => {
                        navigate(
                          `/detail/${process.env.REACT_APP_WELCOME_TOPIC_ID}/1`
                        );
                      }}
                    >
                      <p className="text-[15px] text-[#666666]">
                        أهلا بكم في المجتمع
                      </p>
                      <img src="/images/header/cloud.svg" alt="welcome" />
                    </li> */}

                      {/* notification page */}
                      <li
                        className="flex gap-3 justify-end items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer border-b-[1px] border-[#F1F1F1]"
                        onClick={() => {
                          navigate("/notifications");
                        }}
                      >
                        <p className="text-[15px] text-[#666666]">الإشعارات</p>
                        <img
                          src="/images/header/bell-notification.svg"
                          alt="notification"
                        />
                      </li>
                      <li
                        className="flex gap-3 justify-end items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer border-b-[1px] border-[#F1F1F1]"
                        onClick={(e) => {
                          setIsSettingDropdownOpen(false);
                          navigate("/settings");
                          e.stopPropagation();
                        }}
                      >
                        <p className="text-[15px] text-[#666666]">
                          إعدادات الحساب
                        </p>
                        <img src="/images/header/pencil-edit.svg" alt="edit" />
                      </li>
                      <li
                        className="flex gap-3 justify-end items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer border-b-[1px] border-[#F1F1F1]"
                        onClick={(e) => {
                          e.stopPropagation();
                          logout();
                        }}
                      >
                        <p className="text-[15px] text-[#666666]">
                          تسجيل الخروج
                        </p>
                        <img src="/images/header/logout.svg" alt="logout" />
                      </li>
                      {/* terms and conditions */}
                      {/* <li
                      className="flex gap-3 justify-end items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer border-b-[1px] border-[#F1F1F1]"
                      onClick={() => {
                        navigate(
                          `/detail/${process.env.REACT_APP_TERMS_AND_CONDITIONS_TOPIC_ID}/1`
                        );
                      }}
                    >
                      <p className="text-[15px] text-[#666666]">
                        سياسة وشروط الخدمة
                      </p>
                      <img
                        src="/images/header/terms-and-conditions.svg"
                        alt="terms and conditions"
                      />
                    </li> */}
                    </ul>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
          </div>

          <div className="bell-icon relative sm:gap-5 gap-3 flex items-center">
            {/* Bell Icon */}
            <div
              className="hover:bg-gray-200 relative p-2 sm:p-4 cursor-pointer rounded-lg"
              onClick={handleClickNotificationBell}
            >
              {counts.allCount || report?.length > 0 ? (
                <div
                  className=" text-xs font-medium flex justify-center items-center absolute sm:top-0.5 -top-1
               sm:right-0.5 -right-0.5 bg-[#f65157] text-white rounded-full sm:max-w-6 sm:min-w-6 sm:max-h-6  sm:min-h-6 max-w-5 min-w-5 max-h-5 min-h-5"
                >
                  <p className=" mt-1">
                    {(counts?.allCount ?? 0) +
                      (Array.isArray(report) ? report.length : 0) >
                    9
                      ? "+9"
                      : (counts?.allCount ?? 0) +
                        (Array.isArray(report) ? report.length : 0)}
                  </p>
                </div>
              ) : null}
              <img
                src="/images/header/bell.svg"
                className="w-[18px] h-[20px] cursor-pointer"
                alt="notifications"
              />
            </div>
            {/* Notification Dropdown */}
            <AnimatePresence>
              {isNotificationDropdownOpen && (
                <motion.div
                  variants={popupAntimation}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  ref={dropdownNotificationRef}
                  className="absolute left-[-62px] sm:left-[-10px] sm:right-0 top-[3rem] w-[92vw] sm:w-[480px] bg-white rounded-md border-[1px] border-[#F1F1F1] shadow-lg z-100"
                >
                  {/* Triangle Arrow */}
                  <div className="absolute top-[-9px] left-[78px] sm:left-[27px] sm:right-8 w-4 h-4 bg-white border-l border-t border-[#F1F1F1] transform rotate-45"></div>

                  {/* Dropdown Content */}
                  <ul className="py-2 w-full">
                    <div className="flex flex-col gap-[1rem] items-center pt-[16px] px-[16px]">
                      <div className="w-full flex justify-between px-4">
                        <div className="rounded-full bg-[#FEECEC] py-1 px-3">
                          <span className="text-[#F55157] flex gap-1 text-[12px]">
                            <p>جديدة</p>
                            <p>{unreadCount}</p>
                          </span>
                        </div>
                        <div>
                          <span className="text-[#444444] text-[18px] font-bold">
                            الإشعارات
                          </span>
                        </div>
                      </div>
                      {/* Tabs Container */}
                      <div className="w-full flex justify-end">
                        {tabs.map((tab, index) => (
                          <div
                            key={index}
                            className={`${
                              selectedNotificationTab === tab.tab_name
                                ? "bg-[#BAF3E6]"
                                : ""
                            } px-2 py-1 sm:px-3 sm:py-2 cursor-pointer`}
                            onClick={() => handleTabChange(tab.tab_name)}
                          >
                            <span
                              className={`${
                                selectedNotificationTab === tab.tab_name
                                  ? "text-[#004D5A]"
                                  : "text-[#666666]"
                              } flex gap-1 text-[12px] sm:text-[14px] font-medium`}
                            >
                              {tab.tab_name === "الكل" &&
                                (counts.allCount || 0) + (report?.length || 0)}
                              {tab.tab_name === "ردود" && counts.replyCount}
                              {tab.tab_name === "إعجابات وتفاعل" &&
                                counts.reactionCount}
                              {tab.tab_name === "منشن" && counts.mentionCount}
                              <p>{tab.tab_name}</p>
                            </span>
                          </div>
                        ))}
                      </div>
                      <div
                        className="w-full flex flex-col justify-start max-h-[320px] overflow-y-scroll custom-scrollbar scroll-ms-4"
                        dir="rtl"
                      >
                        {filteredNotifications?.map((notification, index) => (
                          <NotificationItem
                            key={index}
                            notification={notification}
                            report={report}
                            notificationTypeMap={notificationTypeMap}
                            setIsNotificationDropdownOpen={
                              setIsNotificationDropdownOpen
                            }
                            selectedNotificationTab={selectedNotificationTab}
                          />
                        ))}
                      </div>
                      <div className="flex justify-start w-full">
                        <div className="rounded-md bg-[#BAF3E6] px-3 py-1">
                          <span
                            className="#004D5A text-[14px] cursor-pointer"
                            onClick={() => {
                              notificationRedirect();
                              document.body.style.overflow = "unset";
                            }}
                          >
                            إظهار كل التنبيهات
                          </span>
                        </div>
                      </div>
                    </div>
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Search Icon With */}
            <div
              className="hover:bg-gray-200 p-2 sm:p-4 cursor-pointer rounded-lg"
              onClick={() =>
                !window.location.pathname.includes("/category-detail/") &&
                location.pathname !== "/"
                  ? toggleSearchModal()
                  : focusSearchBar()
              }
            >
              <img
                src="/images/header/search.svg"
                className="w-[20px] h-[20px] cursor-pointer"
                alt="search"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex gap-x-3 items-center">
          <a
            href="https://s.salla.sa/register"
            className="sm:flex hidden but but--medium bg-[#004D5A] hover:bg-[#003E4A]"
          >
            <p className="text-secondary-25 text-medium text-[16px]">
              أنشئ متجرك مجانًا
            </p>
          </a>
          <a
            href={process.env.REACT_APP_API_URL + `/auth/oauth2_basic`}
            className="flex but but--medium max-sm:bg-[#004D5A] sm:hover:bg-gray-200"
          >
            <p className="sm:text-primary-l text-secondary-25 transition-all text-medium text-[16px] max-sm:bg-[#004D5A]">
              تسجيل الدخول
            </p>
          </a>
          <button
            className="hover:bg-gray-200 p-2 sm:p-4 cursor-pointer rounded-lg sm:block hidden"
            onClick={() =>
              !window.location.pathname.includes("/category-detail/") &&
              location.pathname !== "/"
                ? toggleSearchModal()
                : focusSearchBar()
            }
          >
            <img
              src="/images/header/search.svg"
              className="w-[20px] h-[20px] cursor-pointer"
              alt="spyglass"
            />
          </button>
        </div>
      )}

      {/* Logo */}
      <div className="flex items-center gap-2">
        {isAdmin && (
          <a
            className="font-bold cursor-pointer md:text-base text-sm"
            href={adminLink}
            target="_blank"
            rel="noreferrer"
          >
            <div className=" flex items-center  gap-1">
              <span>داشبورد</span>
              <img
                src="/images/crown.png"
                alt="crown-icon"
                className=" w-6 mb-2"
              />
            </div>
          </a>
        )}
        <Link to="/">
          <div className="flex items-center">
            <span className="text-[16px] sm:text-[24px] text-[#004956] font-bold text-nowrap">
              مجتمع سلة
            </span>
            <img
              src="/images/logo.svg"
              alt="Salla"
              className="sm:w-[48px] sm:h-[40px] w-[33px] h-[28px] ml-1 sm:ml-2"
            />
          </div>
        </Link>
        <img
          src="/images/header/menu-line.svg"
          alt="toggle sidebar"
          className="sm:hidden block"
          onClick={toggleSidebar}
        />
      </div>
    </header>
  );
};
export default Header;
